import {catchError, EMPTY, filter, Observable, switchMap, take, tap} from 'rxjs';
import {Store} from '@ngxs/store';
import {LoadGlobalPageElementBySlug} from '@store/global-page-element/global-page-element.actions';
import {
  UmpireComponentFooter,
  UmpireComponentHeader,
  UmpireComponentTypeEnum,
  UmpireGlobalPageElementSlugEnum,
  UmpireLinkTypeEnum,
  UmpirePageTypeLink,
} from '@tisa-group/models';
import {GlobalPageElementState} from '@store/global-page-element/global-page-element-state.service';
import {Route, Router} from '@angular/router';
import {routes} from './app-routing.module';
import {PreviewDataInitializer} from '@core/services/preview-data-initializer';
import {RouteNames} from '@core/constants/routes';
import {RouterInitStatusService} from '@core/services/router-init-status.service';

export function routerConfigInitializerFactory(
  store: Store,
  router: Router,
  previewDataInitializer: PreviewDataInitializer,
  routerInitStatusService: RouterInitStatusService,
): () => Observable<unknown> {
  return () => {
    return previewDataInitializer.returnMessageEventIfIframe().pipe(
      switchMap(() =>
        store
          .dispatch(
            new LoadGlobalPageElementBySlug(
              UmpireGlobalPageElementSlugEnum.HEADER_FOOTER,
              {
                include: 'components,components.links',
              },
            ),
          )
          .pipe(
            catchError(() => EMPTY),
            switchMap(() =>
              store.select(
                GlobalPageElementState.getGlobalPageElementBySlug(
                  UmpireGlobalPageElementSlugEnum.HEADER_FOOTER,
                ),
              ),
            ),
            filter(Boolean),
            take(1),
            tap(data => {
              const header = data?.components?.find(
                item => item.kind === UmpireComponentTypeEnum.PAGE_HEADER,
              ) as UmpireComponentHeader;

              const footer = data?.components?.find(
                item => item.kind === UmpireComponentTypeEnum.PAGE_FOOTER,
              ) as UmpireComponentFooter;

              const pageTypes = [
                ...header.links
                  .filter(
                    link => link.resource.data.type === UmpireLinkTypeEnum.PAGE_TYPE,
                  )
                  .map(link => link.resource.data as UmpirePageTypeLink),
                ...footer.links
                  .filter(
                    link => link.resource.data.type === UmpireLinkTypeEnum.PAGE_TYPE,
                  )
                  .map(link => link.resource.data as UmpirePageTypeLink),
              ];

              const mergedLinksWithoutDuplicates = [
                ...new Set([...pageTypes.map(pageType => pageType.attributes.slug)]),
              ];

              const dynamicRoutesBasedOnGlobalPageElements: Route[] =
                mergedLinksWithoutDuplicates.map(slug => ({
                  matcher: url => {
                    if (url.length === 1 && slug === url[0].path) {
                      return {
                        consumed: [],
                      };
                    }

                    return null;
                  },
                  title: pageTypes
                    .filter(pageType => pageType.attributes.slug !== 'home')
                    .find(pageType => pageType.attributes.slug === slug)?.attributes.name,
                  loadChildren: () =>
                    import('@views/view-template-page/view-template-page.module').then(
                      m => m.ViewTemplatePageModule,
                    ),
                }));

              if (routes[0].children) {
                routes[0].children.unshift(...dynamicRoutesBasedOnGlobalPageElements);
              }

              if (routes[0].children) {
                const dynamicRoutesRedirector: Route = {
                  path: '',
                  redirectTo: mergedLinksWithoutDuplicates[0] || RouteNames.search,
                  pathMatch: 'full',
                };

                routes[0].children.unshift(dynamicRoutesRedirector);
              }

              router.resetConfig(routes);
              routerInitStatusService.setInitializerStatus(true);
            }),
          ),
      ),
    );
  };
}
